/**
 * WAF domain hooks — typed CDN domain list + single-domain detail, both scoped
 * by the Owl service path. Backed by `GET /services/:service_id/waf/domains` and
 * `GET /services/:service_id/waf/domains/detail` (tier-2 Mlytics impersonation).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  wafKeys,
  unwrap,
  unwrapList,
  type WafDomain,
  type WafDomainDetail,
  type ListResult,
} from './_shared';

export interface WafDomainListFilters {
  page?: number;
  per_page?: number;
  /** Substring match on the platform/edge domain. */
  search?: string;
  /** Zone status, comma-separated (e.g. `completed,updating`). */
  status?: string;
  /** Platform status, comma-separated (e.g. `in_progress,completed`). */
  platform_status?: string;
  /** Tag names, comma-separated. */
  tag?: string;
  /** CDN platform slugs, comma-separated (e.g. `akamai,aliyun-global`). */
  platform_name?: string;
  /** Origin IPs/hosts, comma-separated. */
  origin_source?: string;
  /** Custom CDN CNAME — substring match on the platform CNAME (`platforms.domain[like]`). */
  cdn_cname?: string;
}

/** List a service's CDN domains (paginated, server-side search). */
export function useWafDomains(
  serviceId: string | undefined,
  filters: WafDomainListFilters = {},
) {
  const params = { ...filters };
  return useQuery<ListResult<WafDomain>>({
    queryKey: wafKeys.domains(serviceId ?? '', filters as Record<string, unknown>),
    enabled: !!serviceId,
    staleTime: 15_000,
    queryFn: async () => unwrapList<WafDomain>(await serviceApiV3.get(`/services/${serviceId}/waf/domains`, { params })),
  });
}

/**
 * Canonical partial patch of a domain's settings (mirrors BE
 * `providers.DomainUpdate`). Every field is optional — only the keys present are
 * applied. The handler maps these vendor-neutral tokens to the Mlytics body.
 */
/**
 * Custom error/challenge page IDs (mirrors BE `DomainCustomPageUpdate`).
 * `0` = the vendor default page. The vendor replaces the whole block, so all
 * fields must be sent together.
 */
export interface WafCustomPageUpdate {
  block: number;
  error_5xx: number;
  rate_limit: number;
  js: number;
  captcha: number;
  maint_enable: boolean;
  maint_page_id: number;
}

/**
 * Partial patch of a domain's origin (upstream) config (mirrors BE
 * `providers.DomainOriginUpdate`). Every field is optional — only the keys
 * present are forwarded to the vendor, so single-field updates are safe.
 */
export interface WafDomainOriginUpdate {
  /** IPv4 | IPv6 | FQDN. */
  type?: string;
  /** Origin addresses/hosts. */
  sources?: string[];
  /** Edge→origin protocol: "Match Viewer" | "HTTP" | "HTTPS". */
  ssl_policies?: string;
  /** Send SNI on HTTPS connections to the origin. */
  sni?: boolean;
  port_http?: number;
  port_https?: number;
  /** consistent | roundrobin | failover. */
  balance_mode?: string;
  keepalive_timeout?: number;
}

export interface WafDomainUpdate {
  ssl_redirect?: boolean;
  enable_http2?: boolean;
  auto_compression?: boolean;
  compress_type?: string;
  ignore_query_string?: boolean;
  websocket_enable?: boolean;
  /** Custom host header forwarded to the origin (vendor `upsHost`). */
  ups_host?: string;
  /** Partial origin/upstream patch (protocol, ports, SNI, …). */
  origin?: WafDomainOriginUpdate;
  /** Custom error/challenge page IDs — sent as one block (vendor replaces all). */
  custom_page?: WafCustomPageUpdate;
  /** Full replacement of the domain's tag names; unknown names are created server-side. */
  tags?: string[];
}

/**
 * Apply a partial settings patch to a domain (`PATCH /waf/domains/settings`).
 * Returns the updated detail, which is written straight into the detail cache;
 * the list is invalidated since status fields may shift.
 */
export function useUpdateWafDomainSettings(
  serviceId: string | undefined,
  domain: string | undefined,
  options?: UseMutationOptions<WafDomainDetail, Error, WafDomainUpdate>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: WafDomainUpdate) =>
      unwrap<WafDomainDetail>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/domains/settings`, patch, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (updated, ...rest) => {
      // The vendor's PATCH echo is stale/partial (e.g. it returns
      // enable_http2:false immediately after enabling it), so DON'T write the
      // echo into the cache — refetch the detail to get the true persisted state.
      if (serviceId && domain) {
        qc.invalidateQueries({ queryKey: wafKeys.domain(serviceId, domain) });
      }
      if (serviceId) {
        qc.invalidateQueries({ queryKey: ['waf', serviceId, 'domains'] });
        // A tag patch may create new account-wide tags — refresh the catalog so
        // the editor's option list picks them up.
        qc.invalidateQueries({ queryKey: wafKeys.tags(serviceId) });
      }
      options?.onSuccess?.(updated, ...rest);
    },
  });
}

/** Build a `{enable}`-body domain toggle hook for a given sub-resource path. */
function makeDomainToggleHook(pathFn: (serviceId: string | undefined) => string) {
  return function useToggle(
    serviceId: string | undefined,
    domain: string | undefined,
    options?: UseMutationOptions<boolean, Error, boolean>,
  ) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (enable: boolean) => {
        await serviceApiV3.put(pathFn(serviceId), { enable }, { params: { domain } });
        return enable;
      },
      ...options,
      onSuccess: (enabled, ...rest) => {
        // These endpoints return no detail; refetch to reflect the new state.
        if (serviceId && domain) {
          qc.invalidateQueries({ queryKey: wafKeys.domain(serviceId, domain) });
        }
        options?.onSuccess?.(enabled, ...rest);
      },
    });
  };
}

/** Toggle HTTP 103 Early Hints for a domain (`PUT /services/:service_id/waf/domains/early-hint`). */
export const useSetWafEarlyHint = makeDomainToggleHook(
  (sid) => `/services/${sid}/waf/domains/early-hint`,
);

/** Toggle OCSP stapling for a domain (`PUT /services/:service_id/waf/domains/ocsp`). */
export const useSetWafOcsp = makeDomainToggleHook(
  (sid) => `/services/${sid}/waf/domains/ocsp`,
);

/**
 * Toggle the WAF / Origin Shield for a domain (`PATCH /waf/domains/waf-enable`).
 * `true` = use Origin Shield, `false` = bypass. Pair with
 * {@link useValidateWafFallback} as a pre-check.
 */
export function useSetWafEnabled(
  serviceId: string | undefined,
  domain: string | undefined,
  options?: UseMutationOptions<boolean, Error, boolean>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enable: boolean) => {
      await serviceApiV3.patch(
        `/services/${serviceId}/waf/domains/waf-enable`,
        { enable },
        { params: { domain } },
      );
      return enable;
    },
    ...options,
    onSuccess: (enabled, ...rest) => {
      if (serviceId && domain) qc.invalidateQueries({ queryKey: wafKeys.domain(serviceId, domain) });
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'domains'] });
      options?.onSuccess?.(enabled, ...rest);
    },
  });
}

/**
 * Pre-check whether toggling the WAF to `wafEnable` would put the domain into
 * origin fallback (`POST /waf/domains/validate-fallback`). Read-only — returns
 * the `is_fallback` flag; does not change state.
 */
export function useValidateWafFallback(
  serviceId: string | undefined,
  domain: string | undefined,
) {
  return useMutation({
    mutationFn: async (wafEnable: boolean) =>
      unwrap<{ is_fallback: boolean }>(
        await serviceApiV3.post(
          `/services/${serviceId}/waf/domains/validate-fallback`,
          { waf_enable: wafEnable },
          { params: { domain } },
        ),
      ).is_fallback,
  });
}

/**
 * Set the domain's global-fallback origin — the address traffic falls back to
 * when no CDN is available (`PATCH /waf/domains/global-fallback`, body `{ip}`).
 * The value may be an IP or an FQDN. Returns the stored fallback.
 */
export function useSetGlobalFallback(
  serviceId: string | undefined,
  domain: string | undefined,
  options?: UseMutationOptions<string, Error, { ip: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ip }: { ip: string }) =>
      unwrap<{ global_fallback: string }>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/domains/global-fallback`, { ip }, {
          params: { domain },
        }),
      ).global_fallback,
    ...options,
    onSuccess: (...args) => {
      if (serviceId && domain) qc.invalidateQueries({ queryKey: wafKeys.domain(serviceId, domain) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Clear the domain's global-fallback origin (`DELETE /waf/domains/global-fallback`). */
export function useClearGlobalFallback(
  serviceId: string | undefined,
  domain: string | undefined,
  options?: UseMutationOptions<void, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/domains/global-fallback`, {
        params: { domain },
      });
    },
    ...options,
    onSuccess: (...args) => {
      if (serviceId && domain) qc.invalidateQueries({ queryKey: wafKeys.domain(serviceId, domain) });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Origin-editor pre-check: reports whether a proposed origin + global-fallback
 * would put the domain into fallback (`POST /waf/domains/validate-origin-fallback`).
 * Read-only — returns the `is_fallback` flag.
 */
export function useValidateOriginFallback(
  serviceId: string | undefined,
  domain: string | undefined,
) {
  return useMutation({
    mutationFn: async (body: {
      origin: { type?: string; sources: string[]; balance_mode?: string };
      global_fallback: string;
    }) =>
      unwrap<{ is_fallback: boolean }>(
        await serviceApiV3.post(`/services/${serviceId}/waf/domains/validate-origin-fallback`, body, {
          params: { domain },
        }),
      ).is_fallback,
  });
}

/** Full configuration of a single domain. */
export function useWafDomainDetail(
  serviceId: string | undefined,
  domain: string | undefined,
) {
  return useQuery<WafDomainDetail>({
    queryKey: wafKeys.domain(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain,
    queryFn: async () =>
      unwrap<WafDomainDetail>(
        await serviceApiV3.get(`/services/${serviceId}/waf/domains/detail`, {
          params: { domain },
        }),
      ),
  });
}
